import { Link } from "react-router-dom";

const inline = (text) => {
  const parts = [];
  const re = /!\[([^\]]*)\]\(([^)\s]+)\)|\*\*(.+?)\*\*|\[(.+?)\]\(([^)\s]+)\)/g;
  let last = 0, m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[2]) parts.push(<img key={m.index} src={m[2]} alt={m[1]} loading="lazy" className="inline-block max-w-full rounded-xl my-2" />);
    else if (m[3]) parts.push(<strong key={m.index}>{m[3]}</strong>);
    else if (m[5].startsWith("/")) parts.push(<Link key={m.index} to={m[5]} className="text-brand underline underline-offset-2">{m[4]}</Link>);
    else parts.push(<a key={m.index} href={m[5]} target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-2 break-all">{m[4]}</a>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const isItem = (l) => /^(\s*[-*]\s+|\s*\d+[.)]\s+)/.test(l);
const isRow = (l) => /^\s*\|.*\|\s*$/.test(l);
const isImage = (l) => /^!\[[^\]]*\]\([^)\s]+\)$/.test(l.trim());
const cells = (l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

const splitBlocks = (text) => {
  const out = [];
  for (const raw of text.split(/\n\s*\n/)) {
    const lines = raw.split("\n");
    let buf = [];
    const flush = () => { if (buf.length) { out.push(buf.join("\n")); buf = []; } };
    for (const l of lines) {
      const prev = buf[buf.length - 1];
      if (prev !== undefined && (isItem(l) !== isItem(prev) || isRow(l) !== isRow(prev))) flush();
      if (/^#{1,3}\s/.test(l.trim()) || isImage(l)) { flush(); out.push(l); continue; }
      buf.push(l);
    }
    flush();
  }
  return out;
};

const Table = ({ lines }) => {
  let rows = lines.filter((l) => !/^\s*\|?\s*:?-{2,}/.test(l)).map(cells);
  if (rows.length > 1 && rows[0].every((c) => !c)) rows = rows.slice(1);
  const [head, ...body] = rows;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead><tr>{head.map((c, i) => <th key={i} className="text-left font-semibold text-stone-900 border-b-2 border-[#cfe0bf] px-3 py-2">{inline(c)}</th>)}</tr></thead>
        <tbody>{body.map((r, i) => <tr key={i} className="odd:bg-[#f7faf3]">{r.map((c, j) => <td key={j} className="border-b border-stone-200 px-3 py-2 align-top">{inline(c)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
};

export const Markdown = ({ text }) => {
  const blocks = splitBlocks(text || "");
  return (
    <div className="space-y-4 text-stone-700 leading-relaxed">
      {blocks.map((b, i) => {
        const t = b.trim();
        if (!t) return null;
        if (t.startsWith("### ")) return <h3 key={i} className="font-semibold text-lg text-stone-900 mt-6">{inline(t.slice(4))}</h3>;
        if (t.startsWith("## ")) return <h2 key={i} className="font-serif text-2xl md:text-3xl text-stone-900 mt-10">{inline(t.slice(3))}</h2>;
        if (t.startsWith("# ")) return <h1 key={i} className="font-serif text-4xl sm:text-5xl text-stone-900">{inline(t.slice(2))}</h1>;
        if (isImage(t)) {
          const m = t.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
          return <figure key={i} className="my-6"><img src={m[2]} alt={m[1]} loading="lazy" className="w-full rounded-2xl shadow-sm" />{m[1] && <figcaption className="text-xs text-stone-500 mt-2">{m[1]}</figcaption>}</figure>;
        }
        const lines = t.split("\n").filter((l) => l.trim());
        if (lines.length > 1 && lines.every(isRow)) return <Table key={i} lines={lines} />;
        if (lines.every(isItem)) {
          const ordered = /^\s*\d+[.)]\s+/.test(lines[0]);
          const Tag = ordered ? "ol" : "ul";
          return <Tag key={i} className={`${ordered ? "list-decimal" : "list-disc"} pl-6 space-y-1`}>{lines.map((l, j) => <li key={j}>{inline(l.trim().replace(/^([-*]|\d+[.)])\s+/, ""))}</li>)}</Tag>;
        }
        return <p key={i}>{t.split("\n").map((l, j, arr) => <span key={j}>{inline(l)}{j < arr.length - 1 && <br />}</span>)}</p>;
      })}
    </div>
  );
};
